import { movieList, tvList } from "@/mocks/media";

export type MediaType = "movie" | "tv";

export interface MediaItem {
  id: string;
  title: string;
  type: MediaType;
  year: number;
  region: string;
  genres: string[];
  rating: number;
  views: number;
  cover: string;
  backdrop: string;
  synopsis: string;
  description: string;
  director: string;
  cast: string[];
  releaseDate: string;
}

export const mediaList = [...movieList, ...tvList] as MediaItem[];

export const REGION_OPTIONS = ["国内", "欧美", "日韩"];
export const YEAR_OPTIONS = [2024, 2023, 2022, 2021];
export const GENRE_OPTIONS = [
  "剧情",
  "动作",
  "喜剧",
  "爱情",
  "科幻",
  "悬疑",
  "奇幻",
  "犯罪",
  "古装",
  "家庭",
  "冒险",
  "青春",
  "动画",
  "美食",
  "医疗",
  "历史",
];

export const PAGE_SIZE = 8;

export function categoryLabel(type: string | undefined): string {
  return type === "tv" ? "电视剧" : "电影";
}

export function getMediaById(id: string | undefined): MediaItem | undefined {
  if (!id) return undefined;
  return mediaList.find((item) => item.id === id);
}

export function getMediaByType(type: string | undefined): MediaItem[] {
  if (type !== "movie" && type !== "tv") return [];
  return mediaList.filter((item) => item.type === type);
}

export function sortByNewest(items: MediaItem[]): MediaItem[] {
  return [...items].sort((a, b) => b.releaseDate.localeCompare(a.releaseDate));
}

export function sortByPopular(items: MediaItem[]): MediaItem[] {
  return [...items].sort((a, b) => b.views - a.views);
}

export interface MediaFilters {
  regions: string[];
  years: number[];
  genres: string[];
}

export function filterMedia(items: MediaItem[], filters: MediaFilters): MediaItem[] {
  return items.filter((item) => {
    const regionOk = filters.regions.length === 0 || filters.regions.includes(item.region);
    const yearOk = filters.years.length === 0 || filters.years.includes(item.year);
    const genreOk =
      filters.genres.length === 0 || item.genres.some((g) => filters.genres.includes(g));
    return regionOk && yearOk && genreOk;
  });
}

export function searchMedia(keyword: string): MediaItem[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return mediaList.filter((item) => {
    const haystack = [
      item.title,
      item.region,
      item.director,
      item.synopsis,
      ...item.genres,
      ...item.cast,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(key);
  });
}

export function getRelatedMedia(item: MediaItem | undefined, limit = 6): MediaItem[] {
  if (!item) return sortByPopular(mediaList).slice(0, limit);
  const scored = mediaList
    .filter((m) => m.id !== item.id)
    .map((m) => {
      const genreScore = m.genres.filter((g) => item.genres.includes(g)).length * 2;
      const regionScore = m.region === item.region ? 1 : 0;
      const typeScore = m.type === item.type ? 1 : 0;
      return { item: m, score: genreScore + regionScore + typeScore };
    })
    .sort((a, b) => b.score - a.score || b.item.views - a.item.views);
  return scored.slice(0, limit).map((s) => s.item);
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 万`;
  return `${views}`;
}

export function paginate<T>(items: T[], page: number, pageSize = PAGE_SIZE): T[] {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

export function totalPages(total: number, pageSize = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / pageSize));
}